import type { StartRealtimeVoiceCallArgs } from './start-realtime-voice-call';
import {
  createRealtimeLeaseLivenessReporter,
  endExpiredRealtimeVoiceCall,
} from './realtime-voice-lease-activity';
import { startLiveVoiceLivenessClock } from '@/lib/live-voice-liveness-clock';
import { retryRealtimeUsageReport } from './realtime-voice-lifecycle';
import type { RealtimeVoiceFailureKind } from './realtime-voice-lifecycle';
import { buildLiveVoiceUsageReport } from '@/lib/live-voice-usage';
import {
  createLiveVoiceTranscriptWriter,
  liveVoiceTranscriptFragment,
} from '@/lib/live-voice-transcripts';

function object(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function createLiveVoiceEventHandlers(
  args: StartRealtimeVoiceCallArgs,
  leaseSessionId: string,
  terminate: (failure: RealtimeVoiceFailureKind) => void
) {
  const transcripts = args.transcriptEnabled
    ? createLiveVoiceTranscriptWriter({
        pending: args.refs.pendingUsageReportsRef.current,
        persist: (fragments) =>
          args.actions.appendTranscript({
            orgThreadId: args.orgThreadId,
            sessionId: leaseSessionId,
            fragments,
          }),
        markIncomplete: () =>
          args.actions.appendTranscript({
            orgThreadId: args.orgThreadId,
            sessionId: leaseSessionId,
            fragments: [],
            incomplete: true,
          }),
      })
    : null;
  let usageChain = Promise.resolve();
  // Set once the server refuses a report because the call already ended.
  let usageEnded = false;
  let finalized = false;
  // A normal end releases the lease before draining final usage (#3926); a
  // late beat result must not end that drain early.
  const ownsLease = () =>
    args.isCurrentCall() &&
    args.refs.leaseSessionIdRef.current === leaseSessionId &&
    !args.refs.sessionRef.current?.isClosing;
  const activity = createRealtimeLeaseLivenessReporter({
    heartbeat: (activityKind) =>
      args.actions.heartbeatLease({
        orgThreadId: args.orgThreadId,
        sessionId: leaseSessionId,
        ...(activityKind ? { activityKind } : {}),
      }),
    isCurrentCall: ownsLease,
    onRenewed: (duration) =>
      args.scheduleLeaseExpiry(duration, () =>
        endExpiredRealtimeVoiceCall(args)
      ),
    // The server ended the lease (e.g. liveness expiry during a network blip).
    onRejected: () => endExpiredRealtimeVoiceCall(args),
    onError: () => console.warn('[voice-call] Live lease heartbeat failed'),
  });
  let clock: { dispose: () => void } | null = null;
  const liveness = {
    start() {
      clock?.dispose();
      clock = startLiveVoiceLivenessClock(() => {
        if (!ownsLease()) {
          liveness.stop();
          return;
        }
        activity.tick();
      });
      activity.tick();
    },
    stop() {
      clock?.dispose();
      clock = null;
    },
  };

  return {
    onTranscriptFlush: (finalized?: boolean) =>
      transcripts?.finish(finalized) ?? Promise.resolve(true),
    onEvent(event: Record<string, unknown>) {
      if (!args.isCurrentCall()) return;
      const fragment = liveVoiceTranscriptFragment(event);
      if (fragment) transcripts?.append(fragment);
      // The server activates the provider call before the session starts.
      if (event.type === 'session.started') liveness.start();
      const usage = buildLiveVoiceUsageReport(event);
      if (usage) {
        // Serial submission preserves report order. The server deduplicates
        // response IDs and cumulative duration independently of this receiver.
        const report = usageChain
          .then(async () => {
            if (usageEnded) return;
            await retryRealtimeUsageReport(async () => {
              const result = await args.actions.reportLiveUsage({
                ...usage,
                orgThreadId: args.orgThreadId,
                sessionId: leaseSessionId,
              });
              if (!result.ended) return;
              usageEnded = true;
              if (ownsLease()) endExpiredRealtimeVoiceCall(args);
            });
          })
          .catch(() => {
            console.warn('[voice-call] Live usage report failed', {
              eventId: usage.eventId,
            });
          })
          .finally(() =>
            args.refs.pendingUsageReportsRef.current.delete(report)
          );
        usageChain = report;
        args.refs.pendingUsageReportsRef.current.add(report);
      }
      if (
        event.type === 'session.input_transcript.delta' &&
        typeof event.delta === 'string' &&
        event.delta.length > 0
      ) {
        args.responses.hasConversationActivityRef.current = true;
        activity.markActivity('user');
      }
      if (event.type === 'session.delegation.created') {
        args.responses.hasConversationActivityRef.current = true;
      }
      if (event.type === 'session.closed') {
        void transcripts?.finish(
          Boolean(args.refs.sessionRef.current?.isClosing)
        );
        finalized = true;
        liveness.stop();
        // A provider expiry or safety termination ends the call even when it
        // supplies final usage. Normal UI close already owns cleanup.
        if (!args.refs.sessionRef.current?.isClosing) {
          terminate('connection');
        }
      }
      if (event.type === 'error' && !args.refs.sessionRef.current?.isClosing) {
        const error = object(event.error);
        console.warn('[voice-call] Live command rejected', {
          eventId:
            typeof event.event_id === 'string' ? event.event_id : undefined,
          hasClientEventId: typeof error?.client_event_id === 'string',
          rejectedContextKind:
            args.refs.sessionRef.current?.getRejectedContextKind(event) ?? null,
        });
      }
    },
    onPlaybackChange(speaking: boolean) {
      if (!args.isCurrentCall()) return;
      args.responses.assistantSpeakingRef.current = speaking;
      args.recording.assistantPlaybackActiveRef.current = speaking;
      args.recording.assistantRecordingActiveRef.current = speaking;
      if (speaking) {
        activity.markActivity('agent');
        args.recording.startTurn(
          'assistant',
          args.recording.remoteStreamRef.current
        );
      } else {
        void args.recording.stopTurn('assistant');
      }
      args.ui.setStatus(speaking ? 'speaking' : 'listening');
      args.responses.onPlaybackChange?.(speaking);
    },
    onInputActivity(active: boolean) {
      if (!args.isCurrentCall()) return;
      args.responses.setUserSpeaking?.(active);
      args.ui.setIsCapturing(active);
      if (active)
        args.recording.startTurn('user', args.recording.micStreamRef.current);
      else void args.recording.stopTurn('user');
    },
    onFailure(reason: string) {
      void transcripts?.finish(false);
      liveness.stop();
      if (!finalized && args.isCurrentCall()) {
        terminate(reason === 'capacity' ? 'capacity' : 'connection');
      }
    },
  };
}
